import { AccountBalance, BarChart, CalendarToday, CardGiftcard, Feed, Filter, Forum, HouseOutlined, Message, MessageOutlined, Person, TrendingUp, VerifiedUser } from "@mui/icons-material";
import React, { Component } from "react";
import { axiosPost } from "../../../utils/AxiosApi";
import { displayErrorAlert } from "../../../utils/Utils";
import { URL } from "../../../utils/Constants";
import moment from "moment/moment";
import "../../../assets/scss/FeedbackModule.scss";
import FeedbackCard from "../../Feedbacks/FeedbackCard";
import ClassPerformance from "./ClassPerformance";
import ListAllTeachers from "../LearningAchievement/ListAllTeachers";
// import { Users, GraduationCap, Building2, TrendingUp, MessageSquare, Calendar, Award, UserCheck, Filter, BarChart3 } from "lucide-react";

// Custom Card Component
const CustomCard = ({ label, index }) => {
  const getIcon = (iconName) => {
    const icons = {
      users: Person,
      graduation: CardGiftcard,
      building: HouseOutlined,
      trending: TrendingUp,
      message: MessageOutlined,
      calendar: CalendarToday,
      award: VerifiedUser,
      filter: Filter,
      barChart3: BarChart,
      school:AccountBalance
    };
    // const IconComponent = icons[iconName] || Users;
    const IconComponent = icons[iconName] || Person;
    return <IconComponent className="w-6 h-6" />;
  };

  return (
     <div className="card dashboard-card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center">
          <div className="flex-grow-1">
            <h6 className="card-subtitle mb-2 text-muted">{label.name}</h6>
            <h3 className="card-title mb-0">{label.value}</h3>
            {label.subCategory && (
              <small className="text-muted d-block mt-1">{label.subCategory}</small>
            )}
            {label.changeText && (
              <small className="text-primary d-block mt-1">{label.changeText}</small>
            )}
          </div>
          <div className="dashboard-icon">
            {getIcon(label.materialIcon)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Teacher Performance Component
const TeacherPerformanceCard = ({ teacher }) => {
  const getBadge = (passRate) => {
    if (passRate >= 80) return "badge-success";
    if (passRate >= 60) return "badge-warning";
    return "badge-danger";
  };
  return (
     <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="card-title mb-1">{teacher.name}</h5>
            <p className="card-text text-muted small">
              {teacher.Class} - {teacher.role}
            </p>
          </div>
          <span className={`badge ${getBadge(teacher.passRate)}`}>
            {teacher.passRate}% Pass Rate
          </span>
        </div>
        <div className="row">
          <div className="col-6 mb-2">
            <small className="text-muted">Total Students</small>
            <p className="mb-0 font-weight-bold">{teacher.totalStudents}</p>
          </div>
          <div className="col-6 mb-2">
            <small className="text-muted">Students Passed</small>
            <p className="mb-0 font-weight-bold">{teacher.studentsPassed}</p>
          </div>
          <div className="col-6 mb-2">
            <small className="text-muted">Average Marks</small>
            <p className="mb-0 font-weight-bold">{teacher.averageMarks}%</p>
          </div>
          <div className="col-6 mb-2">
            <small className="text-muted">Learning Achievement</small>
            <p className="mb-0 font-weight-bold">{teacher.learningAchievement}</p>
          </div>
        </div>
        <div className="border-top pt-2 mt-2">
          <small className="text-muted">
            Next Term Target: <span className="text-primary font-weight-bold">{teacher.nextTermTarget}%</span>
          </small>
        </div>
      </div>
    </div>
  );
};

// Attendance Chart Component
const AttendanceChart = ({ data }) => {
  const getProgressBar = (percentage) => {
    if (percentage >= 90) return "bg-success";
    if (percentage >= 75) return "bg-warning";
    return "bg-danger";
  };
  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title mb-4">Attendance Overview</h5>
        <div>
          {data.map((item, idx) => (
            <div key={idx} className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <span className="small font-weight-bold">{item.category}</span>
                <span className="small text-muted">{item.percentage}%</span>
              </div>
              <div className="progress" style={{ height: "8px" }}>
                <div
                  className={`progress-bar ${getProgressBar(item.percentage)}`}
                  role="progressbar"
                  style={{ width: `${item.percentage}%` }}
                  aria-valuenow={item.percentage}
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
              <div className="d-flex justify-content-between mt-1">
                <small className="text-muted">Present: {item.present}</small>
                <small className="text-muted">Absent: {item.absent}</small>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Special Category Students Component
const SpecialCategoryCard = ({ data }) => {
  return (
    <div className="card">
      <div className="card-body">
        <h5 className="card-title mb-4">Special Category Students</h5>
        <div>
          {data.map((category, idx) => (
            <div key={idx} className="border-left border-primary pl-3 mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="font-weight-bold mb-0">{category.name}</p>
                  <small className="text-muted">Total: {category.total} students</small>
                </div>
                <div className="text-right small">
                  <p className="mb-0 text-muted">Male: {category.male}</p>
                  <p className="mb-0 text-muted">Female: {category.female}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

class DashboardReport extends Component {
  state = {
    // Summary Statistics
    summaryStats: [
      {
        name: "Total Schools",
        value: "110",
        subCategory: "120 Schools",
        materialIcon: "school",
      },
      {
        name: "Total Staff",
        value: "110",
        subCategory: "Coordinators: 10 | Teachers: 100",
        materialIcon: "users",
      },
      {
        name: "Total Students",
        value: "2,450",
        subCategory: "Male: 1,280 | Female: 1,170",
        materialIcon: "users",
      },
      {
        name: "Classes",
        value: "15",
        subCategory: "Nursery to Class 12",
        materialIcon: "building",
      },
    ],
    allTeachers: [
  { id: 1, name: "Ram Kumar Sharma", class: "Class 10", department: "Computer Engineering", role: "Coordinator", subjects: "Computer Science", students: 120, passRate: 90, avgMarks: 78.5, attendance: 96.5, evaluation: 4.5 },
  { id: 2, name: "Sita Kumari Thapa", class: "Class 9", department: "Science", role: "Teacher", subjects: "Physics", students: 95, passRate: 86.3, avgMarks: 75.2, attendance: 94.2, evaluation: 4.2 },
  { id: 3, name: "Hari Prasad Gautam", class: "Class 11", department: "Management", role: "Coordinator", subjects: "Business", students: 110, passRate: 80, avgMarks: 72.8, attendance: 92.8, evaluation: 4.0 },
  { id: 4, name: "Maya Devi Poudel", class: "Class 12", department: "Education", role: "Teacher", subjects: "Pedagogy", students: 88, passRate: 85.2, avgMarks: 76.5, attendance: 95.0, evaluation: 4.3 },
  { id: 5, name: "Krishna Bahadur KC", class: "Class 8", department: "Science", role: "Teacher", subjects: "Biology", students: 85, passRate: 82.5, avgMarks: 74.0, attendance: 93.5, evaluation: 4.1 },
  { id: 6, name: "Radha Sharma", class: "Class 7", department: "Management", role: "Teacher", subjects: "Accounts", students: 78, passRate: 88.0, avgMarks: 77.2, attendance: 96.0, evaluation: 4.4 },
  { id: 7, name: "Bikram Singh Thapa", class: "Nursery", department: "Education", role: "Teacher", subjects: "Early Learning", students: 45, passRate: 100, avgMarks: 85.0, attendance: 98.0, evaluation: 4.7 },
  { id: 8, name: "Anita Kumari Rai", class: "LKG", department: "Education", role: "Teacher", subjects: "Basic Skills", students: 42, passRate: 100, avgMarks: 82.5, attendance: 97.5, evaluation: 4.6 },
  { id: 9, name: "Gopal Prasad Sharma", class: "Class 6", department: "Computer Engineering", role: "Teacher", subjects: "IT Basics", students: 92, passRate: 84.5, avgMarks: 73.8, attendance: 91.2, evaluation: 3.9 },
  { id: 10, name: "Sunita Devi Adhikari", class: "Class 5", department: "Science", role: "Teacher", subjects: "General Science", students: 88, passRate: 86.0, avgMarks: 75.5, attendance: 94.8, evaluation: 4.2 },
  { id: 11, name: "Ramesh Kumar Joshi", class: "Class 4", department: "Management", role: "Teacher", subjects: "Social Studies", students: 82, passRate: 83.0, avgMarks: 72.5, attendance: 92.0, evaluation: 4.0 },
  { id: 12, name: "Kamala Devi Poudel", class: "Class 3", department: "Education", role: "Teacher", subjects: "English", students: 76, passRate: 89.0, avgMarks: 78.0, attendance: 95.5, evaluation: 4.3 },
  { id: 13, name: "Shyam Prasad Bhattarai", class: "Class 2", department: "Science", role: "Teacher", subjects: "Math", students: 70, passRate: 87.5, avgMarks: 76.8, attendance: 93.8, evaluation: 4.1 },
  { id: 14, name: "Laxmi Kumari Tamang", class: "Class 1", department: "Education", role: "Teacher", subjects: "Nepali", students: 65, passRate: 91.0, avgMarks: 79.2, attendance: 96.2, evaluation: 4.4 },
  { id: 15, name: "Narayan Singh Bista", class: "UKG", department: "Education", role: "Teacher", subjects: "Activity Based", students: 48, passRate: 100, avgMarks: 83.5, attendance: 97.8, evaluation: 4.5 },
  { id: 16, name: "Pooja Rajbhandari", class: "Class 10", department: "Science", role: "Teacher", subjects: "Chemistry", students: 115, passRate: 88.5, avgMarks: 77.0, attendance: 95.2, evaluation: 4.4 },
  { id: 17, name: "Deepak Raj Giri", class: "Class 11", department: "Computer Engineering", role: "Teacher", subjects: "Networking", students: 105, passRate: 82.0, avgMarks: 74.2, attendance: 93.0, evaluation: 4.1 },
  { id: 18, name: "Suman Lal Shrestha", class: "Class 12", department: "Management", role: "Coordinator", subjects: "Economics", students: 98, passRate: 84.0, avgMarks: 75.8, attendance: 94.5, evaluation: 4.2 },
  { id: 19, name: "Bina Kumari Shah", class: "Class 9", department: "Education", role: "Teacher", subjects: "History", students: 90, passRate: 87.0, avgMarks: 76.1, attendance: 95.8, evaluation: 4.3 },
  { id: 20, name: "Rajesh Hamal", class: "Class 8", department: "Management", role: "Teacher", subjects: "Marketing", students: 82, passRate: 81.5, avgMarks: 71.9, attendance: 92.5, evaluation: 3.8 },
  { id: 21, name: "Meena Kumari Gurung", class: "Class 7", department: "Science", role: "Teacher", subjects: "Environment", students: 75, passRate: 89.2, avgMarks: 79.0, attendance: 96.4, evaluation: 4.5 },
  { id: 22, name: "Santosh Kumar Yadav", class: "Class 6", department: "Computer Engineering", role: "Teacher", subjects: "C++", students: 88, passRate: 83.4, avgMarks: 74.5, attendance: 93.2, evaluation: 4.0 },
  { id: 23, name: "Sushila Devi Singh", class: "Class 5", department: "Education", role: "Teacher", subjects: "Moral Science", students: 80, passRate: 95.0, avgMarks: 81.2, attendance: 97.0, evaluation: 4.6 },
  { id: 24, name: "Manoj Kumar Jha", class: "Class 4", department: "Science", role: "Teacher", subjects: "Algebra", students: 78, passRate: 85.5, avgMarks: 76.4, attendance: 94.1, evaluation: 4.2 },
  { id: 25, name: "Kalpana Thapa", class: "Class 3", department: "Management", role: "Teacher", subjects: "Civics", students: 72, passRate: 88.8, avgMarks: 78.5, attendance: 95.9, evaluation: 4.3 },
  { id: 26, name: "Arjun Prasad Rijal", class: "Class 2", department: "Education", role: "Teacher", subjects: "Drawing", students: 68, passRate: 98.0, avgMarks: 84.2, attendance: 97.5, evaluation: 4.7 },
  { id: 27, name: "Nirmala Karki", class: "Class 1", department: "Computer Engineering", role: "Teacher", subjects: "Typing", students: 60, passRate: 92.5, avgMarks: 80.0, attendance: 96.8, evaluation: 4.5 },
  { id: 28, name: "Dil Bahadur Magar", class: "UKG", department: "Education", role: "Teacher", subjects: "Rhymes", students: 46, passRate: 100, avgMarks: 88.5, attendance: 98.2, evaluation: 4.8 },
  { id: 29, name: "Saraswati Neupane", class: "LKG", department: "Education", role: "Teacher", subjects: "Numbers", students: 44, passRate: 100, avgMarks: 86.4, attendance: 98.5, evaluation: 4.7 },
  { id: 30, name: "Umesh Mani Dixit", class: "Nursery", department: "Education", role: "Teacher", subjects: "Colors", students: 40, passRate: 100, avgMarks: 87.2, attendance: 99.0, evaluation: 4.9 },
  { id: 31, name: "Prabin Shrestha", class: "Class 10", department: "Management", role: "Teacher", subjects: "Banking", students: 112, passRate: 82.3, avgMarks: 73.5, attendance: 93.5, evaluation: 4.0 },
  { id: 32, name: "Rita Devi Khatri", class: "Class 9", department: "Computer Engineering", role: "Teacher", subjects: "Database", students: 94, passRate: 86.5, avgMarks: 77.8, attendance: 94.4, evaluation: 4.2 },
  { id: 33, name: "Bhola Nath Regmi", class: "Class 11", department: "Science", role: "Teacher", subjects: "Zoology", students: 108, passRate: 84.2, avgMarks: 75.0, attendance: 94.0, evaluation: 4.1 },
  { id: 34, name: "Sajina Shakya", class: "Class 12", department: "Computer Engineering", role: "Teacher", subjects: "Python", students: 96, passRate: 89.0, avgMarks: 79.5, attendance: 95.6, evaluation: 4.4 },
  { id: 35, name: "Ashok Kumar Chaudhary", class: "Class 8", department: "Management", role: "Teacher", subjects: "Business Math", students: 84, passRate: 79.5, avgMarks: 70.8, attendance: 91.5, evaluation: 3.7 },
  { id: 36, name: "Rekha Thapa", class: "Class 7", department: "Education", role: "Teacher", subjects: "Health", students: 80, passRate: 91.4, avgMarks: 80.2, attendance: 96.1, evaluation: 4.5 },
  { id: 37, name: "Suraj Singh Thakuri", class: "Class 6", department: "Science", role: "Teacher", subjects: "Opt. Math", students: 85, passRate: 87.2, avgMarks: 76.5, attendance: 94.8, evaluation: 4.3 },
  { id: 38, name: "Bindu Kumari Shah", class: "Class 5", department: "Education", role: "Teacher", subjects: "Geography", students: 82, passRate: 88.5, avgMarks: 78.4, attendance: 95.3, evaluation: 4.4 },
  { id: 39, name: "Prakash Raj Pathak", class: "Class 4", department: "Computer Engineering", role: "Teacher", subjects: "Hardware", students: 78, passRate: 83.8, avgMarks: 74.2, attendance: 92.5, evaluation: 4.0 },
  { id: 40, name: "Sarita Devi Giri", class: "Class 3", department: "Science", role: "Teacher", subjects: "Nature", students: 75, passRate: 90.0, avgMarks: 79.8, attendance: 96.0, evaluation: 4.5 },
  { id: 41, name: "Keshav Prasad Pandey", class: "Class 2", department: "Management", role: "Teacher", subjects: "Community", students: 70, passRate: 86.4, avgMarks: 75.2, attendance: 93.8, evaluation: 4.1 },
  { id: 42, name: "Tara Devi Bhatta", class: "Class 1", department: "Education", role: "Teacher", subjects: "Grammar", students: 65, passRate: 92.0, avgMarks: 81.5, attendance: 96.5, evaluation: 4.6 },
  { id: 43, name: "Ganesh Bahadur Thapa", class: "UKG", department: "Education", role: "Teacher", subjects: "Games", students: 48, passRate: 100, avgMarks: 89.0, attendance: 98.5, evaluation: 4.8 },
  { id: 44, name: "Sushma Kumari Shrestha", class: "LKG", department: "Education", role: "Teacher", subjects: "Singing", students: 45, passRate: 100, avgMarks: 87.5, attendance: 98.8, evaluation: 4.7 },
  { id: 45, name: "Bishnu Prasad Dhakal", class: "Nursery", department: "Education", role: "Teacher", subjects: "Storytelling", students: 42, passRate: 100, avgMarks: 88.0, attendance: 99.2, evaluation: 4.9 },
  { id: 46, name: "Anju Kumari Verma", class: "Class 10", department: "Education", role: "Teacher", subjects: "English II", students: 118, passRate: 87.5, avgMarks: 77.2, attendance: 95.0, evaluation: 4.3 },
  { id: 47, name: "Mahendra Singh Mahat", class: "Class 11", department: "Management", role: "Teacher", subjects: "Accountancy", students: 102, passRate: 81.2, avgMarks: 72.4, attendance: 92.1, evaluation: 3.9 },
  { id: 48, name: "Indira Devi Joshi", class: "Class 12", department: "Science", role: "Teacher", subjects: "Botany", students: 95, passRate: 85.8, avgMarks: 76.9, attendance: 94.6, evaluation: 4.2 },
  { id: 49, name: "Yubraj Prasad Khanal", class: "Class 9", department: "Computer Engineering", role: "Teacher", subjects: "HTML/CSS", students: 92, passRate: 88.2, avgMarks: 78.4, attendance: 95.5, evaluation: 4.4 },
  { id: 50, name: "Bidhya Devi Bhandari", class: "Class 8", department: "Education", role: "Teacher", subjects: "Social Science", students: 86, passRate: 84.5, avgMarks: 75.1, attendance: 93.8, evaluation: 4.1 },
  { id: 51, name: "Kiran Kumar Rana", class: "Class 7", department: "Science", role: "Teacher", subjects: "Chemistry II", students: 82, passRate: 83.0, avgMarks: 73.8, attendance: 92.5, evaluation: 4.0 },
  { id: 52, name: "Sabitra Devi Mainali", class: "Class 6", department: "Management", role: "Teacher", subjects: "Finance", students: 80, passRate: 86.8, avgMarks: 77.5, attendance: 94.8, evaluation: 4.3 },
  { id: 53, name: "Dinesh Prasad Singh", class: "Class 5", department: "Computer Engineering", role: "Teacher", subjects: "Basic Office", students: 84, passRate: 89.4, avgMarks: 79.2, attendance: 95.8, evaluation: 4.5 },
  { id: 54, name: "Renu Kumari Yadav", class: "Class 4", department: "Education", role: "Teacher", subjects: "Literature", students: 76, passRate: 91.2, avgMarks: 80.5, attendance: 96.5, evaluation: 4.6 },
  { id: 55, name: "Bharat Mani Sharma", class: "Class 3", department: "Science", role: "Teacher", subjects: "Geometry", students: 72, passRate: 85.2, avgMarks: 76.0, attendance: 94.2, evaluation: 4.1 },
  { id: 56, name: "Sita Ram Lamsal", class: "Class 2", department: "Management", role: "Teacher", subjects: "Ethics", students: 68, passRate: 88.0, avgMarks: 78.8, attendance: 95.0, evaluation: 4.3 },
  { id: 57, name: "Anjali Devi Gupta", class: "Class 1", department: "Computer Engineering", role: "Teacher", subjects: "Logic", students: 62, passRate: 84.0, avgMarks: 75.5, attendance: 93.5, evaluation: 4.0 },
  { id: 58, name: "Mohan Prasad Kafle", class: "UKG", department: "Education", role: "Teacher", subjects: "Art", students: 47, passRate: 100, avgMarks: 90.2, attendance: 98.8, evaluation: 4.9 },
  { id: 59, name: "Tulasa Devi Thapa", class: "LKG", department: "Education", role: "Teacher", subjects: "Play", students: 44, passRate: 100, avgMarks: 88.5, attendance: 99.0, evaluation: 4.8 },
  { id: 60, name: "Hari Bahadur KC", class: "Nursery", department: "Education", role: "Teacher", subjects: "Naps", students: 40, passRate: 100, avgMarks: 85.5, attendance: 98.5, evaluation: 4.7 },
  { id: 61, name: "Narendra Kumar Modi", class: "Class 10", department: "Science", role: "Teacher", subjects: "Physics II", students: 110, passRate: 86.5, avgMarks: 76.2, attendance: 94.0, evaluation: 4.2 },
  { id: 62, name: "Mamata Devi Banerjee", class: "Class 9", department: "Management", role: "Teacher", subjects: "Business Law", students: 98, passRate: 80.5, avgMarks: 71.8, attendance: 91.5, evaluation: 3.8 },
  { id: 63, name: "Rahul Singh Gandhi", class: "Class 11", department: "Education", role: "Teacher", subjects: "Sociology", students: 105, passRate: 83.2, avgMarks: 74.5, attendance: 93.2, evaluation: 4.0 },
  { id: 64, name: "Sonia Devi Mainali", class: "Class 12", department: "Computer Engineering", role: "Teacher", subjects: "Cyber Security", students: 94, passRate: 91.5, avgMarks: 82.4, attendance: 96.8, evaluation: 4.6 },
  { id: 65, name: "Arvind Prasad Kejriwal", class: "Class 8", department: "Science", role: "Teacher", subjects: "Electronics", students: 88, passRate: 85.0, avgMarks: 75.8, attendance: 94.2, evaluation: 4.2 },
  { id: 66, name: "Priyanka Kumari Jha", class: "Class 7", department: "Management", role: "Teacher", subjects: "Statistics", students: 80, passRate: 82.8, avgMarks: 73.2, attendance: 92.4, evaluation: 3.9 },
  { id: 67, name: "Amit Kumar Shah", class: "Class 6", department: "Education", role: "Teacher", subjects: "Population", students: 85, passRate: 87.0, avgMarks: 76.5, attendance: 95.0, evaluation: 4.3 },
  { id: 68, name: "Smriti Devi Irani", class: "Class 5", department: "Computer Engineering", role: "Teacher", subjects: "Scratch", students: 82, passRate: 89.0, avgMarks: 79.5, attendance: 96.0, evaluation: 4.5 },
  { id: 69, name: "Nitin Kumar Gadkari", class: "Class 4", department: "Science", role: "Teacher", subjects: "Mechanics", students: 78, passRate: 84.2, avgMarks: 75.0, attendance: 93.8, evaluation: 4.1 },
  { id: 70, name: "Sushma Swaraj", class: "Class 3", department: "Management", role: "Teacher", subjects: "Communication", students: 74, passRate: 92.5, avgMarks: 81.0, attendance: 96.5, evaluation: 4.7 },
  { id: 71, name: "Raj Nath Singh", class: "Class 2", department: "Education", role: "Teacher", subjects: "World History", students: 70, passRate: 86.8, avgMarks: 77.2, attendance: 94.5, evaluation: 4.2 },
  { id: 72, name: "Nirmala Devi Sitharaman", class: "Class 1", department: "Science", role: "Teacher", subjects: "Logic Math", students: 64, passRate: 88.5, avgMarks: 79.4, attendance: 95.5, evaluation: 4.4 },
  { id: 73, name: "Piyush Kumar Goyal", class: "UKG", department: "Education", role: "Teacher", subjects: "Puzzles", students: 48, passRate: 100, avgMarks: 89.5, attendance: 98.8, evaluation: 4.8 },
  { id: 74, name: "Jyotiraditya Scindia", class: "LKG", department: "Education", role: "Teacher", subjects: "Outdoor Play", students: 45, passRate: 100, avgMarks: 86.8, attendance: 98.2, evaluation: 4.7 },
  { id: 75, name: "Shashi Tharoor", class: "Nursery", department: "Education", role: "Teacher", subjects: "Vocab", students: 42, passRate: 100, avgMarks: 91.0, attendance: 99.5, evaluation: 4.9 },
  { id: 76, name: "Bhimsen Thapa", class: "Class 10", department: "Computer Engineering", role: "Teacher", subjects: "JS", students: 115, passRate: 88.0, avgMarks: 78.2, attendance: 95.4, evaluation: 4.4 },
  { id: 77, name: "Jung Bahadur Rana", class: "Class 9", department: "Science", role: "Teacher", subjects: "Military Science", students: 95, passRate: 82.5, avgMarks: 73.0, attendance: 92.8, evaluation: 4.0 },
  { id: 78, name: "Prithvi Narayan Shah", class: "Class 11", department: "Management", role: "Teacher", subjects: "Strategy", students: 108, passRate: 84.8, avgMarks: 75.5, attendance: 94.2, evaluation: 4.2 },
  { id: 79, name: "Bhanubhakta Acharya", class: "Class 12", department: "Education", role: "Teacher", subjects: "Poetry", students: 92, passRate: 91.2, avgMarks: 82.0, attendance: 96.5, evaluation: 4.6 },
  { id: 80, name: "Laxmi Prasad Devkota", class: "Class 8", department: "Education", role: "Teacher", subjects: "Drama", students: 86, passRate: 89.5, avgMarks: 80.4, attendance: 95.8, evaluation: 4.5 },
  { id: 81, name: "Motiram Bhatta", class: "Class 7", department: "Science", role: "Teacher", subjects: "Astrology", students: 82, passRate: 81.0, avgMarks: 72.5, attendance: 92.0, evaluation: 3.9 },
  { id: 82, name: "Parijat Devi", class: "Class 6", department: "Management", role: "Teacher", subjects: "HRM", students: 88, passRate: 85.5, avgMarks: 76.8, attendance: 94.6, evaluation: 4.3 },
  { id: 83, name: "Siddhicharan Shrestha", class: "Class 5", department: "Computer Engineering", role: "Teacher", subjects: "Graphics", students: 84, passRate: 87.2, avgMarks: 77.5, attendance: 95.0, evaluation: 4.4 },
  { id: 84, name: "Lekhnath Paudyal", class: "Class 4", department: "Education", role: "Teacher", subjects: "Writing", students: 80, passRate: 90.5, avgMarks: 81.2, attendance: 96.8, evaluation: 4.6 },
  { id: 85, name: "Madhav Prasad Ghimire", class: "Class 3", department: "Science", role: "Teacher", subjects: "Ecology", students: 76, passRate: 84.8, avgMarks: 75.2, attendance: 93.5, evaluation: 4.1 },
  { id: 86, name: "Bal Krishna Sama", class: "Class 2", department: "Management", role: "Teacher", subjects: "Law", students: 72, passRate: 83.2, avgMarks: 74.0, attendance: 92.8, evaluation: 4.0 },
  { id: 87, name: "Diamond Shrestha", class: "Class 1", department: "Computer Engineering", role: "Teacher", subjects: "Hardware II", students: 68, passRate: 89.0, avgMarks: 79.8, attendance: 95.8, evaluation: 4.5 },
  { id: 88, name: "Tenzing Norgay", class: "UKG", department: "Education", role: "Teacher", subjects: "Adventure", students: 48, passRate: 100, avgMarks: 92.5, attendance: 99.2, evaluation: 4.9 },
  { id: 89, name: "Pasang Lhamu Sherpa", class: "LKG", department: "Education", role: "Teacher", subjects: "Climbing", students: 44, passRate: 100, avgMarks: 88.2, attendance: 98.5, evaluation: 4.8 },
  { id: 90, name: "Anuradha Koirala", class: "Nursery", department: "Education", role: "Teacher", subjects: "Care", students: 42, passRate: 100, avgMarks: 89.5, attendance: 99.0, evaluation: 4.9 },
  { id: 91, name: "Mahabir Pun", class: "Class 10", department: "Computer Engineering", role: "Teacher", subjects: "Wireless", students: 110, passRate: 86.4, avgMarks: 77.0, attendance: 94.5, evaluation: 4.3 },
  { id: 92, name: "Sanduk Ruit", class: "Class 9", department: "Science", role: "Teacher", subjects: "Biology II", students: 96, passRate: 94.5, avgMarks: 85.0, attendance: 97.8, evaluation: 4.8 },
  { id: 93, name: "Binod Chaudhary", class: "Class 11", department: "Management", role: "Teacher", subjects: "Entrepreneurship", students: 105, passRate: 88.5, avgMarks: 79.2, attendance: 95.5, evaluation: 4.5 },
  { id: 94, name: "Kiran KC", class: "Class 12", department: "Education", role: "Teacher", subjects: "Humor", students: 98, passRate: 85.2, avgMarks: 76.5, attendance: 94.0, evaluation: 4.2 },
  { id: 95, name: "Madan Krishna Shrestha", class: "Class 8", department: "Education", role: "Teacher", subjects: "Drama II", students: 90, passRate: 91.0, avgMarks: 82.4, attendance: 96.2, evaluation: 4.6 },
  { id: 96, name: "Hari Bansha Acharya", class: "Class 7", department: "Education", role: "Teacher", subjects: "Satire", students: 88, passRate: 90.8, avgMarks: 81.5, attendance: 96.0, evaluation: 4.6 },
  { id: 97, name: "Narayan Gopal", class: "Class 6", department: "Education", role: "Teacher", subjects: "Music", students: 85, passRate: 95.2, avgMarks: 86.0, attendance: 97.5, evaluation: 4.8 },
  { id: 98, name: "Aruna Lama", class: "Class 5", department: "Education", role: "Teacher", subjects: "Vocals", students: 82, passRate: 93.4, avgMarks: 84.5, attendance: 97.0, evaluation: 4.7 },
  { id: 99, name: "Ambar Gurung", class: "Class 4", department: "Education", role: "Teacher", subjects: "Anthem", students: 80, passRate: 92.5, avgMarks: 83.2, attendance: 96.8, evaluation: 4.7 },
  { id: 100, name: "Gopal Yonzon", class: "Class 3", department: "Education", role: "Teacher", subjects: "Instruments", students: 78, passRate: 91.8, avgMarks: 82.5, attendance: 96.5, evaluation: 4.6 },
  { id: 101, name: "Babu Ram Bhattarai", class: "Class 2", department: "Management", role: "Teacher", subjects: "Planning", students: 75, passRate: 82.0, avgMarks: 73.4, attendance: 92.5, evaluation: 3.9 },
  { id: 102, name: "Gagan Thapa", class: "Class 1", department: "Education", role: "Teacher", subjects: "Debate", students: 72, passRate: 89.4, avgMarks: 80.2, attendance: 95.8, evaluation: 4.5 },
  { id: 103, name: "Rabi Lamichhane", class: "UKG", department: "Education", role: "Teacher", subjects: "Investigation", students: 48, passRate: 100, avgMarks: 87.8, attendance: 98.4, evaluation: 4.6 },
  { id: 104, name: "Balen Shah", class: "Class 10", department: "Computer Engineering", role: "Teacher", subjects: "Urban Planning", students: 112, passRate: 92.5, avgMarks: 84.0, attendance: 97.2, evaluation: 4.8 },
  { id: 105, name: "Harka Sampang", class: "Class 9", department: "Science", role: "Teacher", subjects: "Sustainability", students: 94, passRate: 88.2, avgMarks: 79.5, attendance: 95.8, evaluation: 4.5 },
  { id: 106, name: "Paras Khadka", class: "Class 8", department: "Education", role: "Teacher", subjects: "Sports", students: 86, passRate: 96.0, avgMarks: 88.2, attendance: 98.0, evaluation: 4.9 },
  { id: 107, name: "Sandeep Lamichhane", class: "Class 7", department: "Education", role: "Teacher", subjects: "Cricket", students: 84, passRate: 85.0, avgMarks: 78.4, attendance: 94.5, evaluation: 4.1 },
  { id: 108, name: "Mira Rai", class: "Class 6", department: "Education", role: "Teacher", subjects: "Athletics", students: 82, passRate: 97.5, avgMarks: 90.0, attendance: 99.0, evaluation: 4.9 },
  { id: 109, name: "Manisha Koirala", class: "Class 5", department: "Education", role: "Teacher", subjects: "Acting", students: 80, passRate: 89.2, avgMarks: 81.5, attendance: 96.0, evaluation: 4.4 },
  { id: 110, name: "Shrinkhala Khatiwada", class: "Class 4", department: "Computer Engineering", role: "Teacher", subjects: "Architecture", students: 78, passRate: 93.0, avgMarks: 85.5, attendance: 97.5, evaluation: 4.7 }
],


    // Attendance Data
    attendanceData: [
      { category: "Total Attendance", present: 2315, absent: 135, percentage: 94.5 },
      { category: "Male Students", present: 1205, absent: 75, percentage: 94.1 },
      { category: "Female Students", present: 1110, absent: 60, percentage: 94.9 },
      { category: "Dalit Category", present: 285, absent: 15, percentage: 95.0 },
      { category: "Bipanna Category", present: 180, absent: 20, percentage: 90.0 },
    ],

    // Teacher Performance Data
    topTeachers: [
      {
        name: "Ram Kumar Sharma",
        Class: "Class 10",
        role: "Coordinator",
        totalStudents: 120,
        studentsPassed: 108,
        passRate: 90,
        averageMarks: 78.5,
        learningAchievement: "Excellent",
        nextTermTarget: 92,
      },
      {
        name: "Sita Kumari Thapa",
        Class: "Class 8",
        role: "Teacher",
        totalStudents: 95,
        studentsPassed: 82,
        passRate: 86.3,
        averageMarks: 75.2,
        learningAchievement: "Very Good",
        nextTermTarget: 88,
      },
      {
        name: "Hari Prasad Gautam",
        Class: "Class 10",
        role: "Coordinator",
        totalStudents: 110,
        studentsPassed: 88,
        passRate: 80,
        averageMarks: 72.8,
        learningAchievement: "Good",
        nextTermTarget: 85,
      },
      {
        name: "Maya Devi Poudel",
        Class: "Class 10",
        role: "Teacher",
        totalStudents: 88,
        studentsPassed: 75,
        passRate: 85.2,
        averageMarks: 76.5,
        learningAchievement: "Very Good",
        nextTermTarget: 87,
      },
    ],

    // Feedback Data
    recentFeedback: [
      // {
      //   source: "Student Feedback",
      //   type: "anonymous",
      //   message: "The science lab equipment needs updating. Practical sessions are limited.",
      //   category: "Infrastructure",
      //   date: "2 days ago",
      // },
      // {
      //   source: "Headmaster - School A",
      //   type: "visible",
      //   message: "Teacher training programs have significantly improved teaching quality.",
      //   category: "Teacher Development",
      //   date: "3 days ago",
      // },
      // {
      //   source: "Student Feedback",
      //   type: "visible",
      //   message: "Computer labs are excellent. Would like more programming workshops.",
      //   category: "Academic",
      //   date: "5 days ago",
      // },
      // {
      //   source: "Headmaster - School B",
      //   type: "anonymous",
      //   message: "Need more support for special category students in rural areas.",
      //   category: "Special Support",
      //   date: "1 week ago",
      // },
      // {
      //   source: "Student Feedback",
      //   type: "anonymous",
      //   message: "The science lab equipment needs updating. Practical sessions are limited.",
      //   category: "Infrastructure",
      //   date: "2 days ago",
      // },
      // {
      //   source: "Headmaster - School A",
      //   type: "visible",
      //   message: "Teacher training programs have significantly improved teaching quality.",
      //   category: "Teacher Development",
      //   date: "3 days ago",
      // },
      // {
      //   source: "Student Feedback",
      //   type: "visible",
      //   message: "Computer labs are excellent. Would like more programming workshops.",
      //   category: "Academic",
      //   date: "5 days ago",
      // },
      // {
      //   source: "Headmaster - School B",
      //   type: "anonymous",
      //   message: "Need more support for special category students in rural areas.",
      //   category: "Special Support",
      //   date: "1 week ago",
      // },
    ],

    // Special Category Data
    specialCategories: [
      { name: "Dalit Students", total: 300, male: 165, female: 135 },
      { name: "Bipanna Students", total: 200, male: 110, female: 90 },
      { name: "Scholarship Recipients", total: 180, male: 95, female: 85 },
      { name: "Special Needs", total: 45, male: 25, female: 20 },
    ],

    // Class Performance
    classes: [
  { name: "Nursery", teachers: 4, students: 60, averageScore: 78, passRate: 92 },
  { name: "LKG", teachers: 4, students: 65, averageScore: 77, passRate: 91 },
  { name: "UKG", teachers: 4, students: 70, averageScore: 76, passRate: 90 },

  { name: "Class 1", teachers: 5, students: 80, averageScore: 75, passRate: 89 },
  { name: "Class 2", teachers: 5, students: 85, averageScore: 74, passRate: 88 },
  { name: "Class 3", teachers: 6, students: 90, averageScore: 75, passRate: 87 },
  { name: "Class 4", teachers: 6, students: 95, averageScore: 76, passRate: 86 },
  { name: "Class 5", teachers: 6, students: 100, averageScore: 74, passRate: 88 },

  { name: "Class 6", teachers: 7, students: 120, averageScore: 75, passRate: 86 },
  { name: "Class 7", teachers: 7, students: 140, averageScore: 76, passRate: 85 },
  { name: "Class 8", teachers: 7, students: 160, averageScore: 76, passRate: 85 },

  { name: "Class 9", teachers: 8, students: 180, averageScore: 77, passRate: 86 },
  { name: "Class 10", teachers: 8, students: 220, averageScore: 79, passRate: 87 },

  { name: "Class 11", teachers: 7, students: 200, averageScore: 80, passRate: 88 },
  { name: "Class 12", teachers: 6, students: 190, averageScore: 81, passRate: 89 }
],

    // Additional Stats
    additionalStats: [
      {
        name: "Pending Feedback",
        value: "24",
        changeText: "12 anonymous | 12 visible",
        materialIcon: "message",
      },
      {
        name: "Upcoming Programs",
        value: "8",
        changeText: "3 inter-school activities",
        materialIcon: "calendar",
      },
      // {
      //   name: "Data Collection",
      //   value: "95%",
      //   changeText: "From 15 schools",
      //   materialIcon: "trending",
      // },
      {
        name: "Teacher Evaluations",
        value: "110",
        changeText: "All staff evaluated",
        materialIcon: "award",
      },
      {
        name: "Overall Pass Rate",
        value: "85.2%",
        changeText: "+3.5% from last term",
        materialIcon: "trending",
      },
    ],

    selectedClass: "all",
    selectedCategory: "all",
  };

  handleClassChange = (e) => {
    this.setState({ selectedClass: e.target.value });
  };

  handleCategoryChange = (e) => {
    this.setState({ selectedCategory: e.target.value });
  };

  getFilteredTeachers = () => {
    const { topTeachers, selectedClass } = this.state;
    if (selectedClass === "all") {
      return topTeachers;
    }

     const ClassMap = {
      Nursery: "Nursery",
      LKG: "LKG",
      UKG: "UKG",
      class1: "Class 1",
      class2: "Class 2",
      class3: "Class 3",
      class4: "Class 4",
      class5: "Class 5",
      class6: "Class 6",
      class7: "Class 7",
      class8: "Class 8",
      class9: "Class 9",
      class10: "Class 10",
      class11: "Class 11",
      class12: "Class 12"
    };

    return topTeachers.filter(
      teacher => teacher.Class === ClassMap[selectedClass]
    );
  };

  getFilteredclasses = () => {
    const { classes, selectedClass } = this.state;
    if (selectedClass === "all") {
      return classes;
    }

    const ClassMap = {
      Nursery: "Nursery",
      LKG: "LKG",
      UKG: "UKG",
      class1: "Class 1",
      class2: "Class 2",
      class3: "Class 3",
      class4: "Class 4",
      class5: "Class 5",
      class6: "Class 6",
      class7: "Class 7",
      class8: "Class 8",
      class9: "Class 9",
      class10: "Class 10",
      class11: "Class 11",
      class12: "Class 12"
    };

    return classes.filter(
      dept => dept.name === ClassMap[selectedClass]
    );
  };

  getFilteredAttendance = () => {
    const { attendanceData, selectedCategory } = this.state;
    if (selectedCategory === "all") {
      return attendanceData;
    }

    const categoryMap = {
      dalit: "Dalit Category",
      bipanna: "Bipanna Category",
      scholarship: "Scholarship Recipients",
      special: "Special Needs"
    };

    // For specific categories, show total + the selected category
    return attendanceData.filter(
      item => item.category === "Total Attendance" ||
              item.category === categoryMap[selectedCategory] ||
              item.category === "Male Students" ||
              item.category === "Female Students"
    );
  };

  getFilteredSpecialCategories = () => {
    const { specialCategories, selectedCategory } = this.state;
    if (selectedCategory === "all") {
      return specialCategories;
    }

    const categoryMap = {
      dalit: "Dalit Students",
      bipanna: "Bipanna Students",
      scholarship: "Scholarship Recipients",
      special: "Special Needs"
    };

    return specialCategories.filter(
      category => category.name === categoryMap[selectedCategory]
    );
  };

  getFilteredAllTeachers = () => {
    const { allTeachers, selectedClass } = this.state;
    if (selectedClass === "all") {
      return allTeachers;
    }
    const ClassMap = {
      Nursery: "Nursery",
      LKG: "LKG",
      UKG: "UKG",
      class1: "Class 1",
      class2: "Class 2",
      class3: "Class 3",
      class4: "Class 4",
      class5: "Class 5",
      class6: "Class 6",
      class7: "Class 7",
      class8: "Class 8",
      class9: "Class 9",
      class10: "Class 10",
      class11: "Class 11",
      class12: "Class 12"
    };

    return allTeachers.filter(teacher => teacher.class === ClassMap[selectedClass]);
  };

  componentDidMount(){
    this.getFeedbackList();
  }
  getFeedbackList = () => {
    const param = {
      offset: 0,
      rowCountLimit: 10,
    }
      axiosPost(
        URL.getAllFeedbacks,
        param,
        response => {
          if (response.status === 200) {
            this.setState({
              recentFeedback: response.data.data
            });
          }
        },
        err => {
          displayErrorAlert(err);
        }
      );
    };


  render() {
    const filteredTeachers = this.getFilteredTeachers();
    const filteredclasses = this.getFilteredclasses();
    const filteredAttendance = this.getFilteredAttendance();
    const filteredSpecialCategories = this.getFilteredSpecialCategories();
     const filteredAllTeachers = this.getFilteredAllTeachers();
     const filteredFeedback = this.state.recentFeedback
  ?.filter(f => !f.feedbackTo && f.rating === 0) ?? [];

    return (
      <div className="container-fluid mt-4 dashboard-page-central">
        {/* Header */}
        {/* <div className="row mb-4">
          <div className="col-12">
            <h2 className="page-title">Management Information System Dashboard</h2>
            <p className="text-muted">Educational Performance & Analytics</p>
          </div>
        </div> */}

        {/* Summary Statistics */}
        <div className="row">
          {this.state.summaryStats.map((stat, idx) => (
            <div className="col-md-3 col-sm-6 dashboard-card-50" key={idx}>
              <CustomCard label={stat} index={idx} />
            </div>
          ))}
        </div>

        {/* Additional Statistics */}
        <div className="row" style={{ marginTop: "1rem" }}>
          {this.state.additionalStats.map((stat, idx) => (
            <div className="col-md-3 col-sm-6 dashboard-card-50" key={idx}>
              <CustomCard label={stat} index={idx} />
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="row" style={{ marginTop: "1rem" }}>
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="row align-items-center justify-content-around">
                  <div className="col-auto">
                    <i className="material-icons text-muted">filter_list</i>
                  </div>
                  <div className="col-md-5">
                    <label className="small font-weight-bold">
                      Filter by class
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={this.state.selectedClass}
                      onChange={this.handleClassChange}
                    >
                      <option value="all">All Classes</option>
                      <option value="LKG">LKG</option>
                      <option value="UKG">UKG</option>
                      <option value="class1">Class 1</option>
                      <option value="class2">Class 2</option>
                      <option value="class3">Class 3</option>
                      <option value="class4">Class 4</option>
                      <option value="class5">Class 5</option>
                      <option value="class6">Class 6</option>
                      <option value="class7">Class 7</option>
                      <option value="class8">Class 8</option>
                      <option value="class9">Class 9</option>
                      <option value="class10">Class 10</option>
                      <option value="class11">Class 11</option>
                      <option value="class12">Class 12</option>
                    </select>
                  </div>
                  <div className="col-md-5">
                    <label className="small font-weight-bold">
                      Filter by Category
                    </label>
                    <select
                      className="form-control form-control-sm"
                      value={this.state.selectedCategory}
                      onChange={this.handleCategoryChange}
                    >
                      <option value="all">All Students</option>
                      <option value="dalit">Dalit Category</option>
                      <option value="bipanna">Bipanna Category</option>
                      <option value="scholarship">Scholarship Recipients</option>
                      <option value="special">Special Needs</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="row" style={{ marginTop: "1rem" }}>
          {/* Left Column - Teacher Performance */}
          <div className="col-md-8">
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">Teacher Performance Evaluation</h5>
                  <i className="material-icons text-primary">bar_chart</i>
                </div>
                <div style={{ maxHeight: "500px", overflowY: "auto", overflowX:"hidden" }}>
                  {filteredTeachers.length >0 ?filteredTeachers.map((teacher, idx) => (
                    <TeacherPerformanceCard key={idx} teacher={teacher} />
                  )): (
                    <div className="alert alert-warning">
                      No teachers found for the selected Class.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Class Performance */}
            <ClassPerformance classes={filteredclasses} />
          </div>

          {/* Right Column */}
          <div className="col-md-4">
            {/* Attendance Chart */}
            <div className="mb-4">
              <AttendanceChart data={filteredAttendance} />
            </div>

            {/* Special Categories */}
            <SpecialCategoryCard data={filteredSpecialCategories} />
          </div>
        </div>

        {/* Feedback Section */}
        {}
        <div className="row" style={{ marginTop: "1rem" }}>
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">Recent Feedback from Schools</h5>
                <div className="row">
                  {
                  filteredFeedback.length > 0 ? filteredFeedback.map((feedback, idx) => (
                    <div className="col-md-6" key={idx}>
                      <FeedbackCard feedback={feedback} />
                    </div>
                  )) : (
                    <div className="col-12">
                      <div className="alert alert-info">
                        No feedback available. Please check back later.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row" style={{ marginTop: "1rem" }}>
          <div className="col-12">
            <ListAllTeachers teachers={filteredAllTeachers} />
          </div>
        </div>

        {/* Footer Info */}
        <div className="row" style={{ marginTop: "1rem" }}>
          <div className="col-12">
            <div className="alert alert-info">
              <div className="d-flex align-items-start">
                <i className="material-icons mr-3">emoji_events</i>
                <div>
                  <h6 className="alert-heading">Central System Capabilities</h6>
                  <ul className="mb-0 small">
                    <li>Program creation and management</li>
                    <li>Real-time data collection from all schools</li>
                    <li>Advanced filtering by category (Dalit, Bipanna, etc.)</li>
                    <li>Inter-school activity coordination</li>
                    <li>Anonymous and visible feedback system</li>
                    <li>Comprehensive teacher evaluation metrics</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DashboardReport;